import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import type { ColumnFilters, ColumnFilterValue, TableColumn } from '@app-types';
import {
  COLUMN_FILTER_PREFIX,
  decodeFilterValue,
  encodeFilterValue,
  isActiveFilterValue,
  type DroppedFilter,
} from '../utils/columnFilterUrl';

interface UseColumnFiltersOptions {
  columns: TableColumn[];
  debounceMs?: number;
}

interface UseColumnFiltersReturn {
  /** Current filter values (immediate, for input display) */
  filterValues: ColumnFilters;
  /** Debounced filter values (for API calls) */
  debouncedFilterValues: ColumnFilters;
  /** Set filter value for a specific column; null or '' clears it */
  setFilter: (columnKey: string, value: ColumnFilterValue | null) => void;

  /** Clear all column filters */
  clearAllFilters: () => void;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Get JSON string for API query parameter */
  getApiParam: () => string | null;
  /** Filters dropped from the URL because their value was unusable (FR-015a) */
  droppedFilters: DroppedFilter[];
  /** Acknowledge the dropped-filter notice */
  dismissDroppedFilters: () => void;
}

/**
 * Read every cf_* parameter into filter values, skipping what cannot be used.
 *
 * Lenient by design (FR-015a): an unusable value is dropped and reported, and
 * the rest of the state still loads. A shared link must never be a locked door.
 * The API answers 400 for the same value — a deliberate asymmetry.
 */
function readFiltersFromUrl(
  columns: TableColumn[],
  searchParams: URLSearchParams
): { values: ColumnFilters; dropped: DroppedFilter[] } {
  const values: ColumnFilters = {};
  const dropped: DroppedFilter[] = [];

  columns.forEach((column) => {
    if (column.filterable === false) return;

    const raw = searchParams.get(`${COLUMN_FILTER_PREFIX}${column.key}`);
    if (!raw) return;

    const result = decodeFilterValue(
      column.key,
      raw,
      column.filterType ?? 'text',
      Boolean(column.emptyFilterLabel),
      column.maxFilterLength
    );
    if ('value' in result) {
      values[column.key] = result.value;
    } else {
      dropped.push(result.dropped);
    }
  });

  return { values, dropped };
}

/**
 * Hook for managing per-column filters with URL sync and debounce
 *
 * URL format: ?cf_name=acme&cf_start_date=2026-01-01..2026-03-31&cf_status=Active,Inactive
 * API format: columnFilters={"name":{"kind":"text","value":"acme"}, ...}
 */
export function useColumnFilters(options: UseColumnFiltersOptions): UseColumnFiltersReturn {
  const { columns, debounceMs = 500 } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const initial = useMemo(
    () => readFiltersFromUrl(columns, searchParams),
    // Intentionally read once: later URL changes are handled by the sync effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [filterValues, setFilterValues] = useState<ColumnFilters>(initial.values);
  const [droppedFilters, setDroppedFilters] = useState<DroppedFilter[]>(initial.dropped);

  // Writes we make ourselves must not be echoed back into state by the sync
  // effect below, or a keystroke would be overwritten mid-typing.
  const selfWrite = useRef(false);

  /**
   * Re-read the URL when it changes underneath us — browser Back/Forward, or a
   * link opened in place. Previously the URL was read only in the useState
   * initialiser, so Back updated the address bar but left the inputs stale
   * (FR-019).
   */
  useEffect(() => {
    if (selfWrite.current) {
      selfWrite.current = false;
      return;
    }

    const { values, dropped } = readFiltersFromUrl(columns, searchParams);
    setFilterValues((prev) =>
      JSON.stringify(prev) === JSON.stringify(values) ? prev : values
    );
    if (dropped.length > 0) setDroppedFilters(dropped);
    // `columns` is recreated per render by most callers; keying off the params
    // alone keeps this from looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  /**
   * Only free text waits.
   *
   * A text filter changes on every keystroke, so querying on each one would ask
   * the server about half-typed words. A range or a selection arrives already
   * decided — the range panel does not send anything until Apply — and holding
   * those back for another 500ms just reads as the button having missed the
   * click. Split, so the debounce still covers what it was for.
   */
  const [typed, decided] = useMemo(() => {
    const isTyped = (value: ColumnFilterValue) =>
      typeof value === 'string' || value.kind === 'text';

    const typedValues: ColumnFilters = {};
    const decidedValues: ColumnFilters = {};
    Object.entries(filterValues).forEach(([key, value]) => {
      (isTyped(value) ? typedValues : decidedValues)[key] = value;
    });
    return [typedValues, decidedValues];
  }, [filterValues]);

  const debouncedTyped = useDebounce(typed, debounceMs);
  const debouncedFilterValues = useMemo(
    () => ({ ...debouncedTyped, ...decided }),
    [debouncedTyped, decided]
  );

  const setFilter = useCallback(
    (columnKey: string, value: ColumnFilterValue | null) => {
      // A bare string is the shorthand the other five list pages still pass;
      // normalise it so state holds one shape.
      const normalised: ColumnFilterValue | null =
        typeof value === 'string' ? (value.trim() ? { kind: 'text', value } : null) : value;

      const encoded = normalised === null ? null : encodeFilterValue(normalised);

      setFilterValues((prev) => {
        const next = { ...prev };
        if (encoded === null || normalised === null) {
          delete next[columnKey];
        } else {
          next[columnKey] = normalised;
        }
        return next;
      });

      selfWrite.current = true;
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          const paramKey = `${COLUMN_FILTER_PREFIX}${columnKey}`;
          if (encoded === null) {
            newParams.delete(paramKey);
          } else {
            newParams.set(paramKey, encoded);
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearAllFilters = useCallback(() => {
    setFilterValues({});
    setDroppedFilters([]);

    selfWrite.current = true;
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        columns.forEach((column) => {
          newParams.delete(`${COLUMN_FILTER_PREFIX}${column.key}`);
        });
        return newParams;
      },
      { replace: true }
    );
  }, [columns, setSearchParams]);

  const dismissDroppedFilters = useCallback(() => setDroppedFilters([]), []);

  const hasActiveFilters = useMemo(
    () => Object.values(filterValues).some(isActiveFilterValue),
    [filterValues]
  );

  const getApiParam = useCallback((): string | null => {
    const activeFilters: ColumnFilters = {};

    Object.entries(debouncedFilterValues).forEach(([key, value]) => {
      if (!isActiveFilterValue(value)) return;

      // Use filterKey if specified, otherwise use column key
      const column = columns.find((c) => c.key === key);
      const apiKey = column?.filterKey || key;

      // Send a text filter as the bare-string shorthand. The backend treats the
      // two identically, and keeping the shorthand means the payload the other
      // five list pages send is byte-for-byte unchanged by this feature.
      activeFilters[apiKey] =
        typeof value === 'object' && value.kind === 'text' ? value.value : value;
    });

    if (Object.keys(activeFilters).length === 0) {
      return null;
    }

    return JSON.stringify(activeFilters);
  }, [debouncedFilterValues, columns]);

  return {
    filterValues,
    debouncedFilterValues,
    setFilter,
    clearAllFilters,
    hasActiveFilters,
    getApiParam,
    droppedFilters,
    dismissDroppedFilters
  };
}
